import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { money } from '../../lib/format';
import CrmModal from '../components/CrmModal';
import { IconPlus, IconEdit, IconTrash, IconServices } from '../components/SvgIcons';

export default function ServiceManagement({ currency, onServicesUpdated }) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [toast, setToast] = useState('');

  const [form, setForm] = useState({
    serviceName: '',
    pricingType: 'Fixed',
    defaultPrice: 10000,
    unit: 'Units',
    slabs: [
      { minQty: 1, maxQty: 5, pricePerUnit: 2500 },
      { minQty: 6, maxQty: 10, pricePerUnit: 2200 },
      { minQty: 11, maxQty: 20, pricePerUnit: 2000 },
      { minQty: 21, maxQty: 9999, pricePerUnit: 1800 },
    ],
    description: '',
    status: 'Active',
  });

  const loadServices = () => {
    setLoading(true);
    api.get('/api/client-module/services')
      .then((data) => {
        setServices(data || []);
        onServicesUpdated && onServicesUpdated(data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(loadServices, []);

  const handleSave = async (e) => {
    e?.preventDefault();
    if (!form.serviceName.trim()) return;

    try {
      if (editing) {
        await api.put(`/api/client-module/services/${editing.id}`, form);
        setToast('Service updated successfully');
      } else {
        await api.post('/api/client-module/services', form);
        setToast('Service added successfully');
      }
      setShowModal(false);
      setEditing(null);
      resetForm();
      loadServices();
    } catch (err) {
      alert(err.message || 'Failed to save service');
    }
  };

  const resetForm = () => {
    setForm({
      serviceName: '',
      pricingType: 'Fixed',
      defaultPrice: 10000,
      unit: 'Units',
      slabs: [],
      description: '',
      status: 'Active',
    });
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete service "${name}"?`)) return;
    try {
      await api.del(`/api/client-module/services/${id}`);
      setToast('Service deleted successfully');
      loadServices();
    } catch (err) {
      alert(err.message || 'Failed to delete service');
    }
  };

  const startEdit = (s) => {
    setEditing(s);
    setForm({
      serviceName: s.serviceName || '',
      pricingType: s.pricingType || 'Fixed',
      defaultPrice: s.defaultPrice || 10000,
      unit: s.unit || (s.pricingType === 'Duration' ? 'Months' : 'Units'),
      slabs: s.slabs && s.slabs.length > 0 ? [...s.slabs] : [],
      description: s.description || '',
      status: s.status || 'Active',
    });
    setShowModal(true);
  };

  const addSlabRow = () => {
    setForm((p) => ({
      ...p,
      slabs: [...p.slabs, { minQty: 1, maxQty: 10, pricePerUnit: 2000 }],
    }));
  };

  const updateSlabRow = (index, field, value) => {
    setForm((p) => ({
      ...p,
      slabs: p.slabs.map((sl, i) => (i === index ? { ...sl, [field]: Number(value) } : sl)),
    }));
  };

  const removeSlabRow = (index) => {
    setForm((p) => ({
      ...p,
      slabs: p.slabs.filter((_, i) => i !== index),
    }));
  };

  return (
    <div>
      {toast && (
        <div style={{ background: 'var(--crm-emerald-light)', color: 'var(--crm-emerald)', padding: '12px 18px', borderRadius: 10, marginBottom: 18, fontWeight: 700, border: '1px solid var(--crm-border)' }}>
          ✓ {toast}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.3rem', color: 'var(--crm-text-main)', fontWeight: 800 }}>Dynamic Service Catalog &amp; Pricing Models</h2>
          <div style={{ fontSize: '0.85rem', color: 'var(--crm-text-muted)' }}>Configure Fixed, Quantity Slabs, Duration, and Custom Pricing Rules.</div>
        </div>

        <button
          className="cm-btn-emerald"
          onClick={() => {
            setEditing(null);
            resetForm();
            setShowModal(true);
          }}
        >
          <IconPlus size={16} /> Add New Service
        </button>
      </div>

      <div className="cm-table-card">
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--crm-text-muted)', fontWeight: 600 }}>Loading service catalog…</div>
        ) : (
          <table className="cm-table">
            <thead>
              <tr>
                <th>Service Name</th>
                <th>Pricing Model</th>
                <th>Default Base Rate</th>
                <th>Unit</th>
                <th>Pricing Slabs Configured</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {services.map((s) => (
                <tr key={s.id || s.serviceName}>
                  <td>
                    <div style={{ fontWeight: 800, color: 'var(--crm-text-main)' }}>{s.serviceName}</div>
                    <div style={{ fontSize: '0.775rem', color: 'var(--crm-text-muted)' }}>{s.description || '-'}</div>
                  </td>
                  <td>
                    <span
                      className="chip"
                      style={{
                        background: s.pricingType === 'Quantity' ? '#f1f5f9' : s.pricingType === 'Duration' ? '#f0f4ff' : s.pricingType === 'Custom' ? '#f0f4ff' : '#f1f5f9',
                        color: s.pricingType === 'Quantity' ? '#64748b' : s.pricingType === 'Duration' ? '#1c3d8a' : s.pricingType === 'Custom' ? '#224eae' : '#475569',
                        fontWeight: 700,
                      }}
                    >
                      {s.pricingType || 'Fixed'}
                    </span>
                  </td>
                  <td style={{ fontWeight: 800, color: 'var(--crm-emerald)' }}>{money(s.defaultPrice, currency)}</td>
                  <td>{s.unit || 'Units'}</td>
                  <td>
                    {s.pricingType === 'Quantity' && s.slabs && s.slabs.length > 0 ? (
                      <div style={{ fontSize: '0.775rem' }}>
                        {s.slabs.map((sl, i) => (
                          <div key={i} style={{ color: 'var(--crm-text-muted)' }}>
                            • {sl.minQty}-{sl.maxQty >= 9999 ? '∞' : sl.maxQty} {s.unit}: <strong>{money(sl.pricePerUnit, currency)}</strong>/unit
                          </div>
                        ))}
                      </div>
                    ) : s.pricingType === 'Duration' ? (
                      <span style={{ fontSize: '0.775rem', color: 'var(--crm-emerald)', fontWeight: 600 }}>Monthly Retainer Rate</span>
                    ) : (
                      <span style={{ fontSize: '0.775rem', color: 'var(--crm-text-muted)' }}>Standard Base Rate</span>
                    )}
                  </td>
                  <td>
                    <span className={`cm-badge ${s.status === 'Active' ? 'active-client' : 'inactive-client'}`}>{s.status}</span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="cm-action-icon-btn" title="Edit Service" onClick={() => startEdit(s)}>
                        <IconEdit size={16} />
                      </button>
                      <button className="cm-action-icon-btn" title="Delete Service" style={{ color: 'var(--crm-text-main)' }} onClick={() => handleDelete(s.id, s.serviceName)}>
                        <IconTrash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Reusable CrmModal for Service Catalog */}
      {showModal && (
        <CrmModal
          onClose={() => setShowModal(false)}
          title={editing ? 'Edit Service & Pricing Rules' : 'Add New Service'}
          subtitle="Configure pricing model, quantity slabs, and default base rates"
          icon={IconServices}
          maxWidth={640}
          footer={
            <>
              <button type="button" className="cm-btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="button" className="cm-btn-emerald" onClick={handleSave}>Save Service</button>
            </>
          }
        >
          <form onSubmit={handleSave}>
            <div className="cm-form-section-card">
              <div className="cm-form-section-title">Service Details</div>
              <div className="cm-field-group">
                <label>Service Name *</label>
                <input required value={form.serviceName} onChange={(e) => setForm({ ...form, serviceName: e.target.value })} placeholder="e.g. Reels Creation" />
              </div>

              <div className="cm-grid-2">
                <div className="cm-field-group">
                  <label>Pricing Model *</label>
                  <select
                    value={form.pricingType}
                    onChange={(e) => {
                      const pt = e.target.value;
                      setForm({
                        ...form,
                        pricingType: pt,
                        unit: pt === 'Duration' ? 'Months' : pt === 'Quantity' ? 'Reels' : 'Units',
                        slabs: pt === 'Quantity' && form.slabs.length === 0 ? [
                          { minQty: 1, maxQty: 5, pricePerUnit: 2500 },
                          { minQty: 6, maxQty: 10, pricePerUnit: 2200 },
                          { minQty: 11, maxQty: 9999, pricePerUnit: 2000 },
                        ] : form.slabs,
                      });
                    }}
                  >
                    <option value="Fixed">1. Fixed Price (e.g. Website ₹35,000)</option>
                    <option value="Quantity">2. Quantity Slabs (e.g. 1-5 @ ₹2500, 6-10 @ ₹2200)</option>
                    <option value="Duration">3. Duration Based (e.g. Meta Ads ₹10,000/mo)</option>
                    <option value="Custom">4. Custom Price Override</option>
                  </select>
                </div>

                <div className="cm-field-group">
                  <label>Unit Label</label>
                  <input
                    value={form.unit}
                    onChange={(e) => setForm({ ...form, unit: e.target.value })}
                    placeholder="e.g. Reels, Months, Graphics, Website"
                  />
                </div>
              </div>

              <div className="cm-field-group">
                <label>Default Base Rate (₹)</label>
                <input type="number" value={form.defaultPrice} onChange={(e) => setForm({ ...form, defaultPrice: Number(e.target.value) })} />
              </div>
            </div>

            {/* Dynamic Quantity Slabs Configurator */}
            {form.pricingType === 'Quantity' && (
              <div className="cm-form-section-card" style={{ background: 'var(--crm-bg)', borderColor: 'var(--crm-border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div className="cm-form-section-title" style={{ color: 'var(--crm-text-muted)', margin: 0 }}>Quantity Pricing Slabs</div>
                  <button type="button" className="cm-btn-outline" style={{ padding: '6px 12px', fontSize: '0.8rem' }} onClick={addSlabRow}>＋ Add Slab Row</button>
                </div>

                {form.slabs.map((sl, idx) => (
                  <div className="cm-slab-row" key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 10, alignItems: 'center', marginBottom: 8 }}>
                    <div>
                      <label style={{ fontSize: '0.7rem' }}>Min Qty</label>
                      <input type="number" value={sl.minQty} onChange={(e) => updateSlabRow(idx, 'minQty', e.target.value)} />
                    </div>
                    <div>
                      <label style={{ fontSize: '0.7rem' }}>Max Qty</label>
                      <input type="number" value={sl.maxQty} onChange={(e) => updateSlabRow(idx, 'maxQty', e.target.value)} />
                    </div>
                    <div>
                      <label style={{ fontSize: '0.7rem' }}>Price / Unit (₹)</label>
                      <input type="number" value={sl.pricePerUnit} onChange={(e) => updateSlabRow(idx, 'pricePerUnit', e.target.value)} />
                    </div>
                    <button type="button" style={{ background: 'none', border: 'none', color: 'var(--crm-text-main)', cursor: 'pointer', fontSize: '1rem', marginTop: 14 }} onClick={() => removeSlabRow(idx)}>✕</button>
                  </div>
                ))}
              </div>
            )}

            <div className="cm-form-section-card">
              <div className="cm-field-group">
                <label>Service Description</label>
                <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Service details..." />
              </div>

              <div className="cm-field-group">
                <label>Status</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>
          </form>
        </CrmModal>
      )}
    </div>
  );
}
